import { aisleOf, parseEntry, type Aisle, type KnownArticle } from '@shh/shared';
import { useLens } from '../app/lens';
import { addToList, bringChange, changeItem, removeItem, tickItem } from './api';
import { useFamily } from './family';

/** One article on the list – the same shape for our own list and the Bring! mirror. */
export interface Entry {
  id: string;
  name: string;
  spec: string;
  aisle: Aisle;
}
export type NewEntry = string | { name: string; spec: string };

/** "Oft gekauft" above the list: bought at least this often, at most this many. */
const OFTEN_MIN = 2;
const OFTEN_MAX = 8;

/**
 * Where the list lives: the family's own list (default since 1.20, plain Firestore writes that
 * work offline) or Bring! (the server talks to Bring!, the app reads the mirror). The pages do
 * not care which one it is.
 */
export function useShopping() {
  const f = useFamily();
  const lens = useLens();
  const bring = f.shopping;
  if (bring) {
    const entry = (i: { key: string; uuid?: string; name: string; spec: string }): Entry => ({
      id: i.uuid ?? i.key,
      name: i.name,
      spec: i.spec,
      aisle: aisleOf(i.name),
    });
    return {
      kind: 'bring' as const,
      open: bring.purchase.map(entry),
      often: [] as Entry[],
      recent: bring.recently.map(entry),
      known: [
        ...bring.purchase.map((i) => ({ name: i.name, open: true })),
        ...bring.recently.map((i) => ({ name: i.name })),
      ] as KnownArticle[],
      listName: bring.listName,
      error: bring.error,
      add: (e: NewEntry) =>
        bringChange({ op: 'add', ...(typeof e === 'string' ? parseEntry(e) : e) }),
      tick: (e: Entry) => bringChange({ op: 'done', name: e.name }),
      back: (e: Entry) => bringChange({ op: 'undo', name: e.name }),
      remove: (e: Entry) => bringChange({ op: 'remove', name: e.name }),
      change: undefined,
    };
  }
  const hid = f.householdId;
  const items = f.listItems;
  const bought = items.filter((i) => i.checked);
  // the family's staples first (Mike: "häufig gekaufte Lebensmittel leichter auf die Liste")
  const often = bought
    .filter((i) => (i.count ?? 0) >= OFTEN_MIN)
    .sort((a, b) => (b.count ?? 0) - (a.count ?? 0) || (b.checkedAt ?? 0) - (a.checkedAt ?? 0))
    .slice(0, OFTEN_MAX);
  return {
    kind: 'own' as const,
    open: items.filter((i) => !i.checked).sort((a, b) => a.createdAt - b.createdAt),
    often,
    recent: bought
      .filter((i) => !often.includes(i))
      .sort((a, b) => (b.checkedAt ?? 0) - (a.checkedAt ?? 0))
      .slice(0, 12),
    known: items.map((i) => ({ name: i.name, count: i.count, open: !i.checked })),
    listName: undefined,
    error: null,
    add: (e: NewEntry) => addToList(hid, items, e, lens.member?.id ?? f.meId ?? null),
    tick: (e: Entry) => tickItem(hid, e.id, true),
    back: (e: Entry) => tickItem(hid, e.id, false),
    remove: (e: Entry) => removeItem(hid, e.id),
    change: (e: Entry, patch: Partial<Pick<Entry, 'name' | 'spec' | 'aisle'>>) =>
      changeItem(hid, e.id, patch),
  };
}
